import React, { useState, useEffect, useCallback } from 'react';
import SceneCanvas from './components/scene/SceneCanvas';
import ProjectBrowserModal from './components/modals/ProjectBrowserModal';
import PropertiesPanel from './components/panels/PropertiesPanel';
import SimulationPanel from './components/panels/SimulationPanel';
import ReferenceImagePanel from './components/panels/ReferenceImagePanel';
import ReferenceImageAlignmentPanel from './components/panels/ReferenceImageAlignmentPanel';
import ViewControlsPanel from './components/panels/ViewControlsPanel';
import AlignmentPanel from './components/panels/AlignmentPanel';
import AlignmentHeightFilterPanel from './components/panels/AlignmentHeightFilterPanel';
import LoginModal from './components/modals/LoginModal';
import UserProfileModal from './components/modals/UserProfileModal';
import Header from './components/Header';
import { useSceneStore } from './state/sceneStore';
import { logout, startPollingIfLoggedIn, getProjectById, getCurrentUser } from './api/client';
import { getUserEmailFromToken } from './utils/auth';

import DemoPanel from './components/panels/DemoPanel';
import UpdateAvailableToast from './components/UpdateAvailableToast';
import { useUserStore } from './state/userStore';
import { useSensorPoseUndoRedo } from './hooks/useSensorPoseUndoRedo';

function App() {
  useSensorPoseUndoRedo();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isProjectBrowserOpen, setIsProjectBrowserOpen] = useState(false);
  const [isUserProfileOpen, setIsUserProfileOpen] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const currentProject = useSceneStore(state => state.currentProject);
  const isWallDrawingMode = useSceneStore(state => state.isWallDrawingMode);
  const setIsWallDrawingMode = useSceneStore(state => state.setIsWallDrawingMode);
  const isAlignmentMode = useSceneStore(state => state.isAlignmentMode);
  const setIsAlignmentMode = useSceneStore(state => state.setIsAlignmentMode);
  const referenceImageVisible = useSceneStore(state => state.referenceImageVisible);
  const updateCurrentProject = useSceneStore(state => state.updateCurrentProject);
  const setIsReadOnly = useSceneStore(state => state.setIsReadOnly);
  const alignmentFeatureEnabled = useUserStore(state => state.hasFeature('alignment'));
  const setCurrentUser = useUserStore(state => state.setCurrentUser);
  const clearCurrentUser = useUserStore(state => state.clearCurrentUser);

  // Define loadDemoProject first as it is used by other functions
  const loadDemoProject = useCallback(async () => {
    try {
      setIsReadOnly(true);
      const response = await fetch('/demo_project.json');
      if (response.ok) {
        const demoProject = await response.json();
        // Ensure simulation options are set correctly from the json if needed
        // The store's updateCurrentProject handles this mapping
        updateCurrentProject(demoProject);

        // Explicitly load demo heatmap (pass dummy ID -1, store handles static load if isReadOnly)
        useSceneStore.getState().checkAndLoadCachedHeatmap(-1);

        // If the demo project has a reference image URL relative to the public folder, it works as is.
        // If it's empty, user won't see one.
      } else {
        console.error('Failed to load demo project');
      }
    } catch (error) {
      console.error('Error loading demo project:', error);
    }
  }, [setIsReadOnly, updateCurrentProject]);

  const handleLogout = useCallback(() => {
    logout();
    clearCurrentUser();
    setIsLoggedIn(false);
    setIsProjectBrowserOpen(false);
    setIsUserProfileOpen(false);
    loadDemoProject(); // Reload demo project on logout
  }, [clearCurrentUser, loadDemoProject]);

  const handleAuthError = useCallback((event: CustomEvent) => {
    console.log('Auth error detected:', event.detail);
    handleLogout();
  }, [handleLogout]);

  const handleLoginSuccess = useCallback(async () => {
    try {
      const user = await getCurrentUser();
      setCurrentUser(user);
    } catch (error) {
      console.error('Failed to load user profile after login:', error);
      clearCurrentUser();
    }
    setIsLoggedIn(true);
    setIsReadOnly(false);
    setIsProjectBrowserOpen(true);
  }, [clearCurrentUser, setCurrentUser, setIsReadOnly]);

  const handleAccountDeleted = useCallback(() => {
    handleLogout();
  }, [handleLogout]);

  // Determine if wall drawing is available
  const isWallDrawingAvailable = currentProject?.reference_image_url && referenceImageVisible;

  const setDesignMode = () => {
    setIsWallDrawingMode(false);
    setIsAlignmentMode(false);
  };

  const enterWallDrawingMode = () => {
    setIsAlignmentMode(false);
    setIsWallDrawingMode(true);
  };

  const enterAlignmentMode = () => {
    setIsWallDrawingMode(false);
    setIsAlignmentMode(true);
  };

  // Handle wall drawing mode availability
  useEffect(() => {
    if (isWallDrawingMode && !isWallDrawingAvailable) {
      setIsWallDrawingMode(false);
    }
  }, [isWallDrawingMode, isWallDrawingAvailable, setIsWallDrawingMode]);

  useEffect(() => {
    if (!alignmentFeatureEnabled && isAlignmentMode) {
      setIsAlignmentMode(false);
    }
  }, [alignmentFeatureEnabled, isAlignmentMode, setIsAlignmentMode]);

  const isDesignMode = !isWallDrawingMode && !(isAlignmentMode && alignmentFeatureEnabled);

  const userEmail = getUserEmailFromToken();

  // Initial auth check
  useEffect(() => {
    const checkAuthStatus = async () => {
      const token = localStorage.getItem('accessToken');
      if (!token) {
        setIsLoggedIn(false);
        setIsCheckingAuth(false);
        // Load demo project when not logged in
        loadDemoProject();
        return;
      }

      const authTimeoutMs = 15000;
      const withTimeout = <T,>(promise: Promise<T>, label: string): Promise<T> =>
        Promise.race([
          promise,
          new Promise<T>((_resolve, reject) => {
            window.setTimeout(
              () => reject(new Error(`${label} timed out after ${authTimeoutMs / 1000}s`)),
              authTimeoutMs,
            );
          }),
        ]);

      try {
        const user = await withTimeout(getCurrentUser(), 'Authentication check');
        setCurrentUser(user);

        setIsLoggedIn(true);
        startPollingIfLoggedIn();
        setIsReadOnly(false); // Ensure read-only is off when logged in

        // Restore last project
        const lastProjectId = localStorage.getItem('lastProjectId');
        if (lastProjectId) {
          try {
            const project = await withTimeout(
              getProjectById(parseInt(lastProjectId, 10)),
              'Project restore',
            );
            updateCurrentProject(project);
          } catch (error) {
            console.error('Failed to restore last project:', error);
            localStorage.removeItem('lastProjectId');
            setIsProjectBrowserOpen(true); // Fallback to browser
          }
        } else {
          // No last project, show browser
          setIsProjectBrowserOpen(true);
        }
      } catch (error) {
        console.error('Authentication check failed:', error);
        localStorage.removeItem('accessToken');
        clearCurrentUser();
        setIsLoggedIn(false);
        loadDemoProject();
      } finally {
        setIsCheckingAuth(false);
      }
    };

    checkAuthStatus();
  }, [clearCurrentUser, loadDemoProject, setCurrentUser, setIsReadOnly, updateCurrentProject]);

  // Handle auth errors (token expiry etc)
  useEffect(() => {
    window.addEventListener('authError', handleAuthError as EventListener);
    return () => {
      window.removeEventListener('authError', handleAuthError as EventListener);
    };
  }, [handleAuthError]);

  if (isCheckingAuth) {
    return (
      <div style={{
        display: 'flex',
        width: '100%',
        height: '100vh',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f5f5f5'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '18px', color: '#666', marginBottom: '10px' }}>
            Checking authentication...
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      display: 'flex',
      width: '100%',
      height: '100vh',
      overflow: 'hidden',
      boxSizing: 'border-box',
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0
    }}>
      {isLoggedIn && (
        <Header
          onBrowseProjects={() => setIsProjectBrowserOpen(true)}
          onProfileClick={() => setIsUserProfileOpen(true)}
          onLogout={handleLogout}
          userEmail={userEmail || undefined}
          projectName={currentProject?.name}
        />
      )}

      <div style={{
        flex: 1,
        position: 'relative',
        minWidth: 0,
        height: '100%',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        marginTop: isLoggedIn ? '60px' : '0'
      }}>
        <div style={{ flex: 1, width: '100%', height: '100%', position: 'relative' }}>
          <SceneCanvas />
          <ReferenceImageAlignmentPanel />
        </div>
      </div>

      {/* Side Panel Area */}
      {currentProject && (
        <div style={{
          width: '320px',
          flexShrink: 0,
          background: 'rgba(15, 23, 42, 0.9)',
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          padding: '10px',
          boxSizing: 'border-box',
          gap: '10px',
          height: '100%',
          maxHeight: '100%',
          zIndex: 10 // Ensure it sits above canvas if needed, but below modals
        }}>
          {/* Project Header - Show specifically for logged in users or if we want to show demo title */}
          <div style={{
            background: 'rgba(25, 35, 45, 0.8)',
            color: 'white',
            padding: '15px',
            borderRadius: '8px',
            border: '1px solid #334155',
            textAlign: 'center',
            flex: '0 0 auto',
            minHeight: 0,
          }}>
            <h3 style={{
              margin: 0,
              fontSize: '18px',
              fontWeight: '600',
              color: '#e2e8f0'
            }}>
              {currentProject.name}
            </h3>
          </div>

          {isLoggedIn ? (
            <>
              {/* Mode Tabs */}
              <div style={{
                background: 'rgba(25, 35, 45, 0.8)',
                borderRadius: '8px',
                border: '1px solid #334155',
                overflow: 'hidden',
                flex: '0 0 auto'
              }}>
                <div style={{ display: 'flex' }}>
                  <button
                    onClick={setDesignMode}
                    style={{
                      flex: 1,
                      padding: '12px 16px',
                      background: isDesignMode ? '#4f46e5' : 'transparent',
                      color: isDesignMode ? 'white' : '#9ca3af',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '500',
                      transition: 'all 0.2s ease',
                      borderBottom: isDesignMode ? '2px solid #4f46e5' : '2px solid transparent',
                    }}
                  >
                    📊 Design
                  </button>
                  {isWallDrawingAvailable && (
                    <button
                      onClick={enterWallDrawingMode}
                      style={{
                        flex: 1,
                        padding: '12px 16px',
                        background: isWallDrawingMode ? '#4f46e5' : 'transparent',
                        color: isWallDrawingMode ? 'white' : '#9ca3af',
                        border: 'none',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: '500',
                        transition: 'all 0.2s ease',
                        borderBottom: isWallDrawingMode ? '2px solid #4f46e5' : '2px solid transparent'
                      }}
                    >
                      ✏️ Draw Walls
                    </button>
                  )}
                  {alignmentFeatureEnabled && (
                    <button
                      onClick={enterAlignmentMode}
                      style={{
                        flex: 1,
                        padding: '12px 16px',
                        background: isAlignmentMode ? '#4f46e5' : 'transparent',
                        color: isAlignmentMode ? 'white' : '#9ca3af',
                        border: 'none',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: '500',
                        transition: 'all 0.2s ease',
                        borderBottom: isAlignmentMode ? '2px solid #4f46e5' : '2px solid transparent'
                      }}
                    >
                      🎯 Align
                    </button>
                  )}
                </div>
              </div>

              {/* Conditional Panel Rendering */}
              {isAlignmentMode && alignmentFeatureEnabled ? (
                <>
                  <AlignmentPanel />
                  <ViewControlsPanel />
                  <AlignmentHeightFilterPanel />
                  <ReferenceImagePanel />
                </>
              ) : isWallDrawingMode ? (
                <>
                  <ViewControlsPanel />
                  <ReferenceImagePanel />
                </>
              ) : (
                <>
                  <PropertiesPanel />
                  <ViewControlsPanel />
                  <SimulationPanel />
                  <ReferenceImagePanel />
                </>
              )}
            </>
          ) : (
            <DemoPanel />
          )}
        </div>
      )}

      {/* Modals */}
      {isLoggedIn && (
        <>
          <ProjectBrowserModal isOpen={isProjectBrowserOpen} onClose={() => setIsProjectBrowserOpen(false)} />
          <UserProfileModal
            isOpen={isUserProfileOpen}
            onClose={() => setIsUserProfileOpen(false)}
            onAccountDeleted={handleAccountDeleted}
          />
        </>
      )}

      {!isLoggedIn && (
        <LoginModal isOpen={!isLoggedIn} onLoginSuccess={handleLoginSuccess} />
      )}

      <UpdateAvailableToast />
    </div>
  );
}

export default App;
